import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, OnInit, signal } from '@angular/core';
import { TranslationService } from '../../../../core/services/translation/translation.service';
import { ActivatedRoute } from '@angular/router';
import { formLanguageReloader } from '../../../../core/helpers/page.helper';
import { PageComponent } from '../../../layout/page/page.component';
import {
  closePopupData,
  createErrorPopupData,
  createSuccessPopupData,
  createWarningPopupData,
  LoaderComponent,
  NoValue,
  StatusMessagePopupV2Component
} from '@tribeauto/ngx-mobilite-material';
import { LogService } from '../../../../core/services/log/log.service';
import { ApplicationStore } from '../../../../core/services/application-store.service';
import { AppConfig } from '../../../../app.config';
import { TranslateService } from '@ngx-translate/core';
import { AUTO_CLOSE_DELAY_TEN_SECONDS } from '../../../helpers/popup.helper';

@Component({
  selector: 'pvmo-documents',
  imports: [PageComponent, LoaderComponent, StatusMessagePopupV2Component],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentsComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translationService = inject(TranslationService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly logService = inject(LogService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly appConfig = inject(AppConfig);
  private readonly translateService = inject(TranslateService);

  private readonly DOCUMENTS = 'mobi-edoc-documents';
  private readonly PAGE_NAME = 'page-edoc-documents';

  webStoreId = this.applicationStore.instant('webstoreData')()?.webstoreId ?? '';
  isLoading = signal(true);
  isCreated = false;
  statusPopupElement = closePopupData(createErrorPopupData('', '', '', undefined));

  constructor() {
    effect(() => {
      const selected = this.translationService.selectedFile();
      formLanguageReloader(this.DOCUMENTS, this.PAGE_NAME, selected);
      this.cdr.markForCheck();
    });

    effect(() => {
      const userData = this.applicationStore.instant('userData')();
      let userSub: string | NoValue = undefined;
      if (this.appConfig.devOrLocal) {
        userSub = userData?.login;
      }
      if (this.webStoreId !== this.applicationStore.instant('webstoreData')()?.webstoreId) {
        this.deleteDetailComponent();
        this.isCreated = false;
      }
      this.webStoreId = this.applicationStore.instant('webstoreData')()?.webstoreId ?? '';
      if (!this.isCreated && this.webStoreId) {
        this.isCreated = this.createDetailComponent(userSub);
      }
    });
  }

  private createDetailComponent(userSub: string | NoValue): boolean {
    const detailComponent = document.createElement(this.DOCUMENTS);
    const token = this.activatedRoute.snapshot.queryParamMap.get('token');
    const externalId = this.activatedRoute.parent?.snapshot.paramMap.get('id');
    detailComponent.setAttribute('nonce', 'NGINX_CSP_NONCE');
    detailComponent.setAttribute('webstore', this.webStoreId);
    if (userSub) {
      detailComponent.setAttribute('user-sub', userSub);
    }
    detailComponent.setAttribute('session-uuid', this.logService.sessionUuid());
    if (externalId && token) {
      detailComponent.setAttribute('token', token);
      detailComponent.setAttribute('external-id', externalId);
    }
    detailComponent.setAttribute('language', this.translationService.selectedFile());

    const page = document.getElementById(this.PAGE_NAME);
    if (page && this.webStoreId) {
      this.isLoading.set(false);
      page.append(detailComponent);
      this.cdr.markForCheck();
      return true;
    }
    return false;
  }

  private deleteDetailComponent(): void {
    const detailComponent = document.getElementsByTagName(this.DOCUMENTS);
    const detailComponentArray = Array.from(detailComponent);

    for (const component of detailComponentArray) {
      component.remove();
    }
  }

  ngOnInit(): void {
    const lightColor = this.activatedRoute.snapshot.queryParamMap.get('lightColor');
    if (lightColor) {
      switch (lightColor) {
        case 'V':
          this.setSuccessStatusPopup();
          break;
        case 'O':
          this.setWarningStatusPopup();
          break;
        case 'R':
          this.setErrorStatusPopup();
          break;
        default:
          break;
      }
    }
  }

  closeMessagePopup(): void {
    this.statusPopupElement = closePopupData(this.statusPopupElement);
  }

  private setSuccessStatusPopup(): void {
    this.statusPopupElement = createSuccessPopupData(
      this.translateService.instant('application.documents.popup.green.title'),
      this.translateService.instant('application.documents.popup.green.message'),
      this.translateService.instant('application.documents.popup.close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }

  private setWarningStatusPopup(): void {
    this.statusPopupElement = createWarningPopupData(
      this.translateService.instant('application.documents.popup.orange.title'),
      this.translateService.instant('application.documents.popup.orange.message'),
      this.translateService.instant('application.documents.popup.close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }

  private setErrorStatusPopup(): void {
    this.statusPopupElement = createErrorPopupData(
      this.translateService.instant('application.documents.popup.red.title'),
      this.translateService.instant('application.documents.popup.red.message'),
      this.translateService.instant('application.documents.popup.close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }
}
